import { useEffect, useState, useSyncExternalStore } from 'react';
import useAppearance from '%evp_theme/brandbook/appearance/useAppearance';
import ensureTranslations from './translationLoader';
import storageService, { prefixKey } from './storageService';
import Translator from './translator';
import PAGE_NOTICES from './pageNotices';

const NOTICE_ACTIONS_TRANSLATION_DOMAIN = 'TicketFrontendBundle';

export const NOTICE_CONFIRMED = 'confirmed';
export const NOTICE_SKIPPED = 'skipped';
export const NOTICE_CONFIRM_KEY = 'evp_ticket_frontend.page_notice.confirm';
export const NOTICE_SKIP_KEY = 'evp_ticket_frontend.page_notice.skip';

const SETTLED_OUTCOMES = [NOTICE_CONFIRMED, NOTICE_SKIPPED];

const settledNoticeListeners = new Set();
let noticesSettledSincePageLoad = Object.freeze([]);
let translationDomainsLoadedSincePageLoad = Object.freeze([]);

export function noticeStorageKey(noticeId) {
    return `page-notice:${noticeId}`;
}

const STORED_NOTICE_KEY_PREFIX = prefixKey(noticeStorageKey(''));

function wasSettledInThisBrowser(noticeId) {
    try {
        return SETTLED_OUTCOMES.includes(storageService.getData(noticeStorageKey(noticeId), null));
    } catch (unreadableOutcome) {
        return false;
    }
}

function rememberSettledNotice(noticeId) {
    if (noticesSettledSincePageLoad.includes(noticeId)) {
        return;
    }

    noticesSettledSincePageLoad = Object.freeze(noticesSettledSincePageLoad.concat(noticeId));
    settledNoticeListeners.forEach(listener => listener());
}

function rememberNoticeSettledInAnotherTab({ key }) {
    if (key === null || !key.startsWith(STORED_NOTICE_KEY_PREFIX)) {
        return;
    }

    const noticeId = key.slice(STORED_NOTICE_KEY_PREFIX.length);
    if (wasSettledInThisBrowser(noticeId)) {
        rememberSettledNotice(noticeId);
    }
}

function subscribeToSettledNotices(listener) {
    if (settledNoticeListeners.size === 0) {
        window.addEventListener('storage', rememberNoticeSettledInAnotherTab);
    }
    settledNoticeListeners.add(listener);

    return () => {
        settledNoticeListeners.delete(listener);
        if (settledNoticeListeners.size === 0) {
            window.removeEventListener('storage', rememberNoticeSettledInAnotherTab);
        }
    };
}

function readNoticesSettledSincePageLoad() {
    return noticesSettledSincePageLoad;
}

function settle(noticeId, outcome) {
    storageService.setData(noticeStorageKey(noticeId), outcome);
    rememberSettledNotice(noticeId);
}

export function resetPageLoadNoticeState() {
    noticesSettledSincePageLoad = Object.freeze([]);
    translationDomainsLoadedSincePageLoad = Object.freeze([]);
}

function findDueNotice(settledSincePageLoad) {
    return PAGE_NOTICES.find(notice => (
        !settledSincePageLoad.includes(notice.id) && notice.isDue() && !wasSettledInThisBrowser(notice.id)
    )) || null;
}

function translationDomainsOf(notice) {
    return notice === null ? [] : [...new Set([NOTICE_ACTIONS_TRANSLATION_DOMAIN, ...notice.translationDomains])];
}

function useNoticeTextsLoaded(notice) {
    const missingDomains = translationDomainsOf(notice)
        .filter(domain => !translationDomainsLoadedSincePageLoad.includes(domain));
    const missingDomainsKey = missingDomains.join();
    const [, setLastLoadedDomainsKey] = useState(null);

    useEffect(() => {
        if (missingDomains.length > 0) {
            ensureTranslations(missingDomains).then(() => {
                translationDomainsLoadedSincePageLoad = Object.freeze(
                    translationDomainsLoadedSincePageLoad.concat(missingDomains),
                );
                setLastLoadedDomainsKey(missingDomainsKey);
            });
        }
    }, [missingDomainsKey]);

    return missingDomains.length === 0;
}

export default function usePageNotice(area) {
    const settledSincePageLoad = useSyncExternalStore(subscribeToSettledNotices, readNoticesSettledSincePageLoad);
    const notice = findDueNotice(settledSincePageLoad);
    const { enabled: appearanceChoiceAvailable } = useAppearance();
    const textsLoaded = useNoticeTextsLoaded(notice);

    if (notice === null || !textsLoaded) {
        return null;
    }

    return {
        id: notice.id,
        ...notice.describe({ area, appearanceChoiceAvailable }),
        confirmLabel: Translator.trans(NOTICE_CONFIRM_KEY),
        skipLabel: Translator.trans(NOTICE_SKIP_KEY),
        confirm: () => settle(notice.id, NOTICE_CONFIRMED),
        skip: () => settle(notice.id, NOTICE_SKIPPED),
    };
}
